'use client';
import { motion } from 'motion/react';
import {
  TrendingUp,
  AlertTriangle,
  Users,
  Filter,
  MessageCircle,
  Send,
  IndianRupee,
  BarChart3,
  MessageSquareText,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface Feature {
  label: string;
  title: string;
  description: string;
  mockup: React.ReactNode;
  accentColor: string; // Top border color for the mockup card
}

const features: Feature[] = [
  {
    label: 'Briefing',
    title: 'Your morning intelligence brief',
    description:
      'Auto-detected anomalies naming specific people and problems. Revenue opportunities ranked by ₹-impact. Team health at a glance. All computed before you arrive.',
    mockup: <BriefingMockup />,
    accentColor: 'hsl(30 95% 52%)',
  },
  {
    label: 'Explore',
    title: 'Every answer, one drill-down away',
    description:
      'Sort, filter, group, and compare across salesmen, retailers, SKUs, distributors, and beats. Territory drill-down. No pivot tables. No Excel. Ever again.',
    mockup: <ExploreMockup />,
    accentColor: 'hsl(160 60% 35%)',
  },
  {
    label: 'Ask',
    title: 'Ask in English. Get numbers, not opinions.',
    description:
      'Type a question in English or Hindi. Get deterministic answers backed by real computation — not LLM hallucinations. 11 intent types, from root-cause analysis to what-if scenarios.',
    mockup: <AskMockup />,
    accentColor: 'hsl(260 50% 50%)',
  },
  {
    label: 'Act',
    title: 'From insight to WhatsApp in 60 seconds',
    description:
      'Create targeted schemes, send via WhatsApp, and track delivery down to read receipts. 28 pre-built templates. Incrementality measurement with holdout groups.',
    mockup: <ActMockup />,
    accentColor: 'hsl(140 55% 42%)',
  },
];

export default function Features() {
  return (
    <section
      id="features"
      className="py-24 sm:py-32"
      aria-labelledby="features-heading"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          className="max-w-2xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Features
          </p>
          <h2
            id="features-heading"
            className="mt-4 font-serif text-3xl font-700 leading-tight text-foreground sm:text-4xl md:text-5xl"
          >
            Built for how sales leaders think.
          </h2>
        </motion.div>

        <div className="mt-20 space-y-28 sm:mt-24 sm:space-y-36">
          {features.map((f, i) => (
            <div
              key={f.label}
              className={`flex flex-col gap-10 lg:items-center lg:gap-16 ${i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'
                }`}
            >
              {/* Text */}
              <motion.div
                className="flex-shrink-0 lg:w-[340px] xl:w-[380px]"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5 }}
                variants={fadeUp}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: f.accentColor }}
                    aria-hidden="true"
                  />
                  <p
                    className="text-xs font-semibold uppercase tracking-[0.2em]"
                    style={{ color: f.accentColor }}
                  >
                    {f.label}
                  </p>
                </div>
                <h3 className="mt-4 font-serif text-2xl font-700 text-foreground sm:text-3xl">
                  {f.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {f.description}
                </p>
              </motion.div>

              {/* Mockup — the star, with colored top accent */}
              <motion.div
                className="min-w-0 flex-1"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: 0.12 }}
                variants={fadeUp}
              >
                <div
                  className="overflow-hidden rounded-2xl border border-border bg-white shadow-lg"
                  style={{
                    borderTopWidth: '3px',
                    borderTopColor: f.accentColor,
                  }}
                >
                  {f.mockup}
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Mini Mockups ────────────────────────────────────── */

function BriefingMockup() {
  return (
    <div className="p-5 sm:p-6" aria-hidden="true">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-foreground">Good morning, Rajesh</p>
          <p className="text-[10px] text-muted-foreground">Mar 2026 &middot; Day 18 of 31</p>
        </div>
        <div className="flex gap-1.5">
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-[9px] font-semibold text-red-700">2 Critical</span>
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-semibold text-amber-700">5 Warnings</span>
        </div>
      </div>

      <div className="mb-4 rounded-lg border border-amber-200/60 bg-amber-50/60 p-2.5">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-[10px] font-semibold text-amber-900">3 salesmen below 80% coverage threshold</p>
            <p className="mt-0.5 text-[9px] text-amber-700">Mohammed Khan at 65.5% — declining 4 months</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {[
          { label: 'Revenue MTD', value: '₹2.4Cr', delta: '+12.3%', up: true },
          { label: 'Collection', value: '87.2%', delta: '+3.1%', up: true },
          { label: 'Coverage', value: '78.4%', delta: '-2.8%', up: false },
        ].map((m) => (
          <div key={m.label} className="rounded-lg border border-border/40 bg-[hsl(40,30%,98%)] p-3">
            <p className="text-[9px] text-muted-foreground">{m.label}</p>
            <p className="mt-0.5 text-base font-bold text-foreground">{m.value}</p>
            <p className={`text-[9px] font-semibold ${m.up ? 'text-emerald-600' : 'text-red-500'}`}>{m.delta}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-lg border border-accent/20 bg-accent/5 p-3">
        <div className="flex items-center gap-2">
          <IndianRupee className="h-3.5 w-3.5 text-accent" />
          <div>
            <p className="text-[10px] font-semibold text-foreground">₹18.5L revenue opportunity</p>
            <p className="text-[9px] text-muted-foreground">142 dormant retailers, 8 under-penetrated SKUs</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExploreMockup() {
  const rows = [
    { name: 'Deepak Verma', region: 'North', rev: '₹8.2L', cov: '92%', status: 'on-track' },
    { name: 'Priya Sharma', region: 'West', rev: '₹6.1L', cov: '85%', status: 'on-track' },
    { name: 'Mohammed Khan', region: 'South', rev: '₹3.7L', cov: '65%', status: 'critical' },
    { name: 'Anjali Patel', region: 'East', rev: '₹5.4L', cov: '79%', status: 'warning' },
    { name: 'Vikram Singh', region: 'North', rev: '₹7.1L', cov: '88%', status: 'on-track' },
  ];

  return (
    <div className="p-5 sm:p-6" aria-hidden="true">
      <div className="mb-4 flex items-center gap-2">
        <span className="rounded-md bg-foreground px-2.5 py-1 text-[10px] font-semibold text-background">Salesman</span>
        <div className="flex items-center gap-1 rounded-md border border-border px-2 py-1">
          <Filter className="h-2.5 w-2.5 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">All Territories</span>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border px-2 py-1">
          <Users className="h-2.5 w-2.5 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">47 salesmen</span>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border/60">
        <table className="w-full text-[10px] sm:text-[11px]">
          <thead>
            <tr className="border-b border-border/60 bg-secondary/50">
              <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Name</th>
              <th className="hidden px-3 py-2.5 text-left font-semibold text-muted-foreground sm:table-cell">Region</th>
              <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Revenue</th>
              <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Coverage</th>
              <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-b border-border/30 last:border-0">
                <td className="px-3 py-2.5 font-medium text-foreground">{r.name}</td>
                <td className="hidden px-3 py-2.5 text-muted-foreground sm:table-cell">{r.region}</td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-foreground">{r.rev}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-foreground">{r.cov}</td>
                <td className="px-3 py-2.5 text-center">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-semibold ${r.status === 'on-track' ? 'bg-emerald-50 text-emerald-700' :
                      r.status === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                    }`}>
                    {r.status === 'on-track' ? 'On Track' : r.status === 'warning' ? 'Attention' : 'Critical'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AskMockup() {
  return (
    <div className="flex flex-col p-5 sm:p-6" aria-hidden="true">
      <div className="space-y-3">
        {/* User */}
        <div className="flex justify-end">
          <div className="max-w-[72%] rounded-2xl rounded-br-md bg-foreground px-4 py-2.5 text-[11px] text-background">
            Why is Tamil Nadu underperforming this month?
          </div>
        </div>

        {/* AI */}
        <div className="flex gap-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15">
            <BarChart3 className="h-3.5 w-3.5 text-accent" />
          </div>
          <div className="max-w-[85%] space-y-2.5 rounded-2xl rounded-bl-md border border-border bg-white px-4 py-3">
            <p className="text-[11px] font-medium text-foreground">
              Tamil Nadu revenue is ₹42.3L MTD, <strong className="text-red-600">23% below target</strong>. Three root causes:
            </p>
            <div className="space-y-1.5">
              {[
                { cause: 'Coverage drop', detail: '3 salesmen below 70%', impact: '₹8.2L' },
                { cause: 'Dormant retailers', detail: '89 shops, 30+ days idle', impact: '₹5.1L' },
                { cause: 'SKU gaps', detail: 'Rice Premium at 12% penetration', impact: '₹3.4L' },
              ].map((c) => (
                <div key={c.cause} className="flex items-center justify-between rounded-md bg-[hsl(40,30%,98%)] px-3 py-2">
                  <div>
                    <p className="text-[10px] font-semibold text-foreground">{c.cause}</p>
                    <p className="text-[9px] text-muted-foreground">{c.detail}</p>
                  </div>
                  <span className="font-mono text-[10px] font-bold text-red-600">{c.impact}</span>
                </div>
              ))}
            </div>
            <p className="text-[9px] text-muted-foreground">
              <span className="font-semibold text-foreground">Confidence: 94%</span> &middot; Deterministic engine
            </p>
          </div>
        </div>

        {/* Chips */}
        <div className="flex flex-wrap gap-1.5 pl-10">
          {['Show the 3 salesmen', 'What about Bangalore?', 'Create a scheme'].map((s) => (
            <span key={s} className="rounded-full border border-border bg-white px-2.5 py-1 text-[9px] font-medium text-foreground">
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActMockup() {
  return (
    <div className="p-5 sm:p-6" aria-hidden="true">
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2">
        <MessageSquareText className="h-3.5 w-3.5 text-accent" />
        <p className="text-[10px] font-medium text-foreground">
          Pre-filled from Ask: Dormant Retailer Reactivation
        </p>
      </div>

      {/* Steps indicator */}
      <div className="mb-5 flex items-center gap-3">
        {['Type', 'Audience', 'Send'].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${i === 2 ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground'
              }`}>{i + 1}</div>
            <span className={`text-[10px] ${i === 2 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{s}</span>
            {i < 2 && <div className="h-px w-6 bg-border" />}
          </div>
        ))}
      </div>

      {/* WhatsApp preview */}
      <div className="rounded-xl bg-[#e5ddd5] p-4">
        <div className="mx-auto max-w-sm rounded-lg bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-[10px] font-semibold text-emerald-800">WhatsApp Preview</span>
          </div>
          <p className="text-[10px] leading-relaxed text-gray-800">
            Hi <strong>{'{name}'}</strong>, we noticed you haven&apos;t ordered recently.
            Order ₹5,000+ this week for 5% extra discount. Your salesman <strong>{'{salesman}'}</strong> will visit tomorrow.
          </p>
        </div>
      </div>

      {/* Send bar */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground">
          <strong className="text-foreground">142</strong> recipients &middot; Est. <strong className="text-foreground">₹71</strong>
        </p>
        <div className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-[10px] font-semibold text-white">
          <Send className="h-3 w-3" />
          Send via WhatsApp
        </div>
      </div>
    </div>
  );
}
